import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useRete } from 'rete-react-plugin'
import {
  Alert,
  borderRadius,
  Button,
  colors,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NotificationLevel,
  notify,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import { AreaExtensions } from 'rete-area-plugin'
import { useApolloClient } from '@apollo/client'
import faker from 'faker'
import { FormattedMessage } from 'react-intl'
import { VerticalDivider } from '../../../../pages/quality-audit-workflow-modify/CatalogAdminQualityAuditWorkflowModifyPage.tsx'
import { useDomainMessages } from '../../../../utils/domain/intl.ts'
import PipelineFormStepSelectModal from '../PipelineFormStepSelectModal.tsx'
import { fetchStepVersion } from '../../../../api/getBulkStepVersion.hooks.ts'
import {
  GetListStepVersionsDocument,
  type GetListStepVersionsQuery,
  type GetListStepVersionsQueryVariables,
  type Instacart_Catalog_Catflow_V1_StepVersion,
} from '../../../../api'
import { Spacer } from '../../../../utils/common.styles.tsx'
import {
  AttributeManagementCategoryEnum,
  type PipelineStep,
} from '../../../../types/attributeManagement.types.ts'
import {
  CAMP_CLIENT_ID,
  type PipelineEditorAttributes,
  type PipelineEditorGraphInputStep,
} from './types.ts'
import { useGenerateGraphInput } from './util/generateGraphInput.hooks.tsx'
import { createEditor } from './rete/editor.tsx'
import { type PipelineEditorProcessedState } from './rete/types.ts'

const PipelineEditorContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '800px',
  width: '100%',
  border: `2px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: borderRadius.X8,
  zIndex: `99`,
  position: 'relative',
})

const PipelineEditorHeaderContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  borderBottom: `1px solid ${colors.GRAYSCALE.X30}`,
  boxShadow: `0 5px 5px rgba(0, 0, 0, 0.15)`,
  padding: `${spacing.X12}`,
  gap: spacing.X12,
  zIndex: 10,
})

const PipelineEditorRenderer = styled.div({
  height: '100%',
  width: '100%',
  borderBottomLeftRadius: borderRadius.X8,
  borderBottomRightRadius: borderRadius.X8,
})

const PipelineEditorBodyContainer = styled.div({
  height: '100%',
  width: '100%',
  display: 'flex',
  overflow: 'scroll',
})

const PipelineEditorFocusCheck = styled.div({
  position: 'absolute',
  inset: '0px',
  backgroundColor: 'rgb(0 0 0 / 5%)',
  zIndex: 10,
  cursor: 'pointer',
  display: 'flex',
  height: '100%',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
})

const PipelineEditorErrorContainer = styled.div({
  borderLeft: `1px solid ${colors.GRAYSCALE.X30}`,
  flexDirection: 'column',
  boxShadow: `-5px 0px 5px rgba(0, 0, 0, 0.10)`,
  backgroundColor: 'white',
  zIndex: 2,
  display: 'flex',
  height: '100%',
  width: '30%',
  alignItems: 'flex-start',
  gap: `${spacing.X16}`,
  padding: `${spacing.X20}`,
  overflowX: 'hidden',
  overflowY: 'scroll',
})

export interface PipelineEditorProps {
  attributes: PipelineEditorAttributes
  onChange: (step: PipelineStep) => void
  onDelete: (step: PipelineStep) => void
  onProcessed: (state: PipelineEditorProcessedState) => void
  disabled: boolean
  showDebugTooltips?: boolean
  getStepVersionsCache?: (stepId: string) => GetListStepVersionsQuery | undefined
}

export const PipelineEditor: React.FC<{
  attributes: PipelineEditorAttributes
  disabled: boolean
  onChange: (attributes: PipelineEditorAttributes) => void
  onError?: (hasError: boolean) => void
}> = props => {
  const client = useApolloClient()
  const [graphInput, isLoading, addToBulkStepVersionCache] = useGenerateGraphInput(props.attributes)
  const refInitialSteps = useRef<PipelineStep[] | null>(null)
  const [graphProcessedState, setGraphProcessedState] = useState<PipelineEditorProcessedState>({
    isValid: false,
    errors: [],
  })
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const [showInputModal, setShowInputModal] = useState<boolean>(false)

  const [showAddModal, setShowAddModal] = useState(false)
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isHovering, setIsHovering] = useState(true)
  const [showDebugTooltips, setShowDebugTooltips] = useState(false)

  // Cache for step versions indexed by stepId
  const stepVersionsCache = useRef<Map<string, GetListStepVersionsQuery>>(new Map())
  const pendingFetches = useRef<Map<string, Promise<GetListStepVersionsQuery>>>(new Map())
  const lastValidation = useRef<PipelineEditorProcessedState | undefined>(undefined)

  useEffect(() => {
    if (isLoading || refInitialSteps.current) return
    // We need to create a deep copy of the initial version to do resets
    refInitialSteps.current = JSON.parse(JSON.stringify(props.attributes.steps)) as PipelineStep[]
  }, [isLoading, props.attributes.steps])

  // Function to fetch and cache step versions
  const fetchAndCacheStepVersions = useCallback(
    async (stepId: string): Promise<GetListStepVersionsQuery> => {
      // Check if already cached
      if (stepVersionsCache.current.has(stepId)) {
        return stepVersionsCache.current.get(stepId)!
      }

      // Check if fetch is already in progress
      if (pendingFetches.current.has(stepId)) {
        return pendingFetches.current.get(stepId)!
      }

      // Start new fetch
      const idempotencyKey = faker.random.uuid()
      const fetchPromise = client
        .query<GetListStepVersionsQuery, GetListStepVersionsQueryVariables>({
          query: GetListStepVersionsDocument,
          variables: {
            stepId,
            clientId: CAMP_CLIENT_ID,
            idempotencyKey,
          },
          fetchPolicy: 'cache-first',
        })
        .then(response => {
          const data = response.data
          stepVersionsCache.current.set(stepId, data)
          pendingFetches.current.delete(stepId)
          return data
        })
        .catch(error => {
          pendingFetches.current.delete(stepId)
          throw error
        })

      pendingFetches.current.set(stepId, fetchPromise)
      return fetchPromise
    },
    [client]
  )

  // Getter function for cache access
  const getStepVersionsCache = useCallback(
    (stepId: string) => stepVersionsCache.current.get(stepId),
    []
  )

  // Pre-fetch step versions for all steps in the pipeline
  useEffect(() => {
    if (isLoading || !graphInput) return

    const stepIds = new Set<string>()
    Object.values(graphInput.pipelineStepIdToStepMap).forEach(item => {
      const stepId = item.step?.stepId
      if (stepId) {
        stepIds.add(stepId)
      }
    })

    stepIds.forEach(stepId => {
      if (!stepVersionsCache.current.has(stepId)) {
        fetchAndCacheStepVersions(stepId).catch(() => {
          // Silently fail, nodes will handle errors individually
        })
      }
    })
  }, [graphInput, isLoading, fetchAndCacheStepVersions])

  const triggerSave = useCallback(
    (pipelineStepIdToStepMap: { [p: string]: PipelineEditorGraphInputStep }) => {
      const steps: PipelineStep[] = []
      for (const step of Object.values(pipelineStepIdToStepMap ?? {})) {
        steps.push(step.step)
      }
      props.onChange({
        pipelineVersion: 2,
        ...props.attributes,
        steps,
      })
    },
    [props]
  )

  const onDeleteStep = useCallback(
    (step: PipelineStep) => {
      if (!step || !step.pipelineStepId || !graphInput) return
      const newGraphState = {
        ...graphInput,
      }
      delete newGraphState.pipelineStepIdToStepMap[step.pipelineStepId]
      triggerSave(newGraphState.pipelineStepIdToStepMap)
    },
    [graphInput, triggerSave]
  )

  const onUpsertStep = useCallback(
    (step: PipelineStep, stepVersion?: Instacart_Catalog_Catflow_V1_StepVersion) => {
      if (!step || !step.pipelineStepId || !graphInput) return
      const newGraphState = {
        ...graphInput,
      }

      if (!newGraphState.pipelineStepIdToStepMap[step.pipelineStepId]) {
        newGraphState.pipelineStepIdToStepMap[step.pipelineStepId] = {
          step: step,
        }
      }

      newGraphState.pipelineStepIdToStepMap[step.pipelineStepId].step = step
      if (stepVersion) {
        newGraphState.pipelineStepIdToStepMap[step.pipelineStepId].stepVersion = stepVersion
      }

      triggerSave(newGraphState.pipelineStepIdToStepMap)
    },
    [graphInput, triggerSave]
  )

  const onProcessed = useCallback(
    (state: PipelineEditorProcessedState) => {
      // IMPORTANT!
      // We must diff the state given to us from rete as rete renders multiple times in a react state change.
      // To avoid infinite re-renders we must diff what is given to us to avoid repeated calls.
      if (
        state.isValid != graphProcessedState.isValid ||
        state.errors.length != graphProcessedState.errors.length
      ) {
        setGraphProcessedState({
          ...graphProcessedState,
          isValid: state.isValid,
          errors: state.errors,
        })
        props.onError?.(!state.isValid)
      }
    },
    [graphProcessedState, props]
  )

  const i18n = useDomainMessages({
    addStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.addStep',
    center: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.center',
    arrange:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.arrange',
    node: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.node',
    issues: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.issues',
    reset: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.reset',
    save: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.save',
    debug: 'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.menu.debug',
    invalidConnection:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.error.invalidConnection',
    resetConfirmTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.resetConfirm.title',
    resetConfirmMessage:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.resetConfirm.message',
    resetConfirmCancel:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.resetConfirm.cancel',
    resetConfirmReset:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineEditor.resetConfirm.reset',
  })

  const create = React.useCallback(
    (el: HTMLElement) =>
      createEditor(
        el,
        isLoading ? null : graphInput,
        {
          ...props,
          onChange: onUpsertStep,
          onDelete: onDeleteStep,
          onProcessed: state => onProcessed(state),
          showDebugTooltips,
          getStepVersionsCache,
        },
        () => {
          notify({
            level: NotificationLevel.Error,
            body: <>{i18n.invalidConnection}</>,
          })
        }
      ),
    [
      isLoading,
      graphInput,
      props,
      onUpsertStep,
      onDeleteStep,
      showDebugTooltips,
      getStepVersionsCache,
      onProcessed,
      i18n.invalidConnection,
    ]
  )
  const [ref, app] = useRete(create)

  const highlightNode = useCallback(
    (nodeId: string) => {
      const element = document.getElementById(nodeId)
      if (!element) return

      // Center the view on the node
      if (app?.area && app?.editor) {
        const node = app.editor.getNode(nodeId)
        if (node) {
          AreaExtensions.zoomAt(app.area, [node])
        }
      }

      // Store original styles
      const originalBoxShadow = element.style.boxShadow
      const originalTransition = element.style.transition

      // Add transition for smooth animation
      element.style.transition = 'box-shadow 0.2s ease-in-out'
      element.style.boxShadow = `0px 0px 0px 4px ${colors.DETRIMENTAL.REGULAR}`

      const flashInterval = setInterval(() => {
        clearInterval(flashInterval)
        element.style.boxShadow = originalBoxShadow
        element.style.transition = originalTransition
      }, 300)
    },
    [app]
  )

  return (
    <PipelineEditorContainer
      onMouseEnter={() => {
        if (isFocused) return
        setIsHovering(true)
      }}
      onMouseLeave={() => {
        if (isFocused) return
        setIsHovering(false)
      }}
    >
      {showInputModal && (
        <PipelineFormStepSelectModal
          category={AttributeManagementCategoryEnum.Input}
          onClose={() => {
            setShowInputModal(false)
          }}
          onConfirm={updatedStep => {
            const type = updatedStep?.type ?? ''
            onUpsertStep({
              ...updatedStep,
              type: type,
            })
            setShowInputModal(false)
          }}
        />
      )}
      {showResetConfirmModal && (
        <Modal isOpen={true} onRequestClose={() => setShowResetConfirmModal(false)}>
          <ModalContainer>
            <ModalHeader>{i18n.resetConfirmTitle}</ModalHeader>
            <ModalBody>
              <Text>{i18n.resetConfirmMessage}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="tertiary" onClick={() => setShowResetConfirmModal(false)}>
                {i18n.resetConfirmCancel}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (refInitialSteps.current) {
                    props.onChange({
                      ...props.attributes,
                      steps: refInitialSteps.current as PipelineStep[],
                      pipelineVersion: 2,
                    })
                  }
                  setShowResetConfirmModal(false)
                }}
              >
                {i18n.resetConfirmReset}
              </Button>
            </ModalFooter>
          </ModalContainer>
        </Modal>
      )}
      <PipelineEditorHeaderContainer>
        <Button
          variant={'tertiary'}
          onClick={() => {
            if (!app?.area || !app?.editor) return
            AreaExtensions.zoomAt(app.area, app.editor.getNodes())
          }}
        >
          {i18n.center}
        </Button>
        <Button
          disabled={props.disabled}
          variant={'tertiary'}
          onClick={() => {
            if (!app?.arrange) return
            app.arrange()
          }}
        >
          {i18n.arrange}
        </Button>
        <Button
          disabled={props.disabled}
          variant={'tertiary'}
          onClick={() => {
            if (isLoading || !refInitialSteps.current) return
            setShowResetConfirmModal(true)
          }}
        >
          {i18n.reset}
        </Button>
        <Button
          variant={showDebugTooltips ? 'secondary' : 'tertiary'}
          onClick={() => setShowDebugTooltips(!showDebugTooltips)}
        >
          {i18n.debug}
        </Button>
        <VerticalDivider />
        <Button disabled={props.disabled} variant={'primary'} onClick={() => setShowAddModal(true)}>
          {i18n.addStep}
        </Button>
        <Spacer />
        <Button
          disabled={props.disabled}
          variant={!showErrors ? 'tertiary' : 'secondary'}
          onClick={() => setShowErrors(!showErrors)}
          css={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: '8px' }}
        >
          <Text
            weight={'semibold'}
            css={{
              color: !props.disabled ? colors.SECONDARY.REGULAR : 'inherit',
            }}
          >
            {i18n.issues}:
          </Text>
          <Text
            weight={'semibold'}
            css={{
              color: props.disabled
                ? 'inherit'
                : graphProcessedState.errors.length > 0
                ? colors.DETRIMENTAL.REGULAR
                : colors.PRIMARY.REGULAR,
            }}
          >
            {graphProcessedState.errors.length}
          </Text>
        </Button>
      </PipelineEditorHeaderContainer>
      {!isFocused && isHovering && <PipelineEditorFocusCheck onClick={() => setIsFocused(true)} />}
      <PipelineEditorBodyContainer>
        <PipelineEditorRenderer ref={ref} />
        {showErrors && (
          <PipelineEditorErrorContainer>
            {graphProcessedState.errors.map((error, index) => (
              <Alert
                key={`${error.pipelineStepId}-${index}`}
                variant={'warning'}
                css={{ width: `100%` }}
              >
                <p>
                  {error.pipelineStepId && (
                    <a
                      onClick={() => highlightNode(error.pipelineStepId!)}
                      css={{ color: colors.PRIMARY.REGULAR, cursor: 'pointer' }}
                    >
                      {i18n.node}
                    </a>
                  )}
                  <FormattedMessage id={error.messageId} />
                </p>
              </Alert>
            ))}
          </PipelineEditorErrorContainer>
        )}
      </PipelineEditorBodyContainer>
      {showAddModal && (
        <PipelineFormStepSelectModal
          category={null}
          includedCategories={[
            AttributeManagementCategoryEnum.Input,
            AttributeManagementCategoryEnum.Control,
          ]}
          onClose={() => {
            setShowAddModal(false)
          }}
          onConfirm={async step => {
            try {
              if (!step) {
                setShowAddModal(false)
                return
              }

              const newStep: PipelineStep = {
                type: step.type,
                attributeOverrides: step?.attributeOverrides ?? {},
                pipelineStepId: crypto.randomUUID(),
              }

              // Fetch the specific step version (for creating the step)
              let stepVersion: Instacart_Catalog_Catflow_V1_StepVersion | undefined = undefined
              if (step.stepId && step.version) {
                const idempotencyKey = faker.random.uuid()
                const idemKey = idempotencyKey + step.stepId + step.version
                const stepVersionResult = await fetchStepVersion(client, {
                  stepId: step.stepId,
                  version: step.version,
                  clientId: CAMP_CLIENT_ID,
                  idempotencyKey: idemKey,
                })

                const response =
                  stepVersionResult.data.instacart_catalog_catflow_v1_CatflowAPI_GetStepVersion
                stepVersion = response?.stepVersion

                if (!stepVersion) {
                  setShowAddModal(false)
                  return
                }

                // Update the bulk step version cache (single version)
                addToBulkStepVersionCache(step.stepId, stepVersion)

                // Fetch and cache all versions for this step (for "update to latest version" functionality)
                if (step.stepId && !stepVersionsCache.current.has(step.stepId)) {
                  fetchAndCacheStepVersions(step.stepId)
                }

                if (stepVersion?.version) {
                  newStep.version = stepVersion.version
                }

                if (stepVersion?.stepId) {
                  newStep.stepId = stepVersion.stepId
                }
              }

              if (step?.stepVersionId) {
                newStep.stepVersionId = step.stepVersionId
              }

              if (step?.nextStepsRule) {
                newStep.nextStepsRule = step.nextStepsRule
              }

              onUpsertStep(newStep, stepVersion)
              setShowAddModal(false)
            } catch (error) {
              setShowAddModal(false)
            }
          }}
        />
      )}
    </PipelineEditorContainer>
  )
}
